import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  mutation createSubject($data: createSubjectInput!) {
    createSubject(data: $data) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.MutationResponseState = {
  result: null,
  error: null,
  loading: false
};

export const createSubjectRequest = createAsyncThunk<
  TYPES.MutationResponse,
  {data: TYPES.CreateSubjectInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'subject/create_subject',
  async (params: {data: TYPES.CreateSubjectInput;}, { extra: client }) => {
    try {
      const response = await client.mutate({
        mutation: GRAPHQL,
        variables: params
      });
      return response.data.createSubject;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const createSubjectSlice = createSlice({
  name: 'create_subject',
  initialState: initialState,
  reducers: {
    createSubjectClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(createSubjectRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(createSubjectRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(createSubjectRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { createSubjectClear } = createSubjectSlice.actions;

export const createSubject = createSelector(
  (state: RootState) => state.subject.createSubject.result,
  (state: RootState) => state.subject.createSubject.error,
  (state: RootState) => state.subject.createSubject.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default createSubjectSlice.reducer;